// `process.env.MAPBOX_TOKEN` is replaced at build time by webpack DefinePlugin
// (this is a browser bundle with no @types/node), so declare the shape locally.
declare const process: { env: Record<string, string | undefined> };

// Mapbox public token (pk.*). Public by design — always visible in the client
// bundle; the abuse control is a URL restriction on the Mapbox account, not
// secrecy (pentest A-06). Sourced from the build env (`MAPBOX_TOKEN`, baked by
// webpack DefinePlugin) so it isn't hardcoded in source and can be rotated by
// rebuild. A runtime `window.__MAPBOX_TOKEN__` override takes precedence.
export const MAPBOX_TOKEN: string =
  (typeof window !== 'undefined' && (window as any).__MAPBOX_TOKEN__) ||
  process.env.MAPBOX_TOKEN ||
  '';

export interface MapboxFeature {
  /** Full formatted address e.g. "1 Queen Street, Auckland 1010, New Zealand". */
  place_name: string;
  /** Headline text for the feature — typically the street/place name only. */
  text: string;
  /** Street number (only set for `place_type: ["address"]` features). */
  address?: string;
  /** [lon, lat]. */
  center: [number, number];
  /** Hierarchical parents — region/place/country. Each entry has `id` like
   *  "place.123" and a `text` field with the human label. */
  context?: Array<{ id: string; text: string }>;
}

export interface AddressParts {
  addressLine1: string;
  addressLine2: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  country: string;
}

/** Resolve city label from a Mapbox feature (`place.*` context entry). */
export function extractCity(feature: MapboxFeature): string {
  const ctx = feature.context ?? [];
  const place = ctx.find((c) => String(c.id).startsWith('place.'));
  return place?.text ?? '';
}

/** Decompose a Mapbox feature into the same shape as our `addressSchema`
 *  subform. For street-level features Mapbox splits the house number
 *  into `feature.address`; we recombine it with `feature.text` so line1
 *  reads naturally (e.g. "1 Queen Street").
 *
 *  Suburb / locality handling: Mapbox surfaces NZ suburbs (e.g. "Milford"
 *  in "11 Wairau Road, Milford, Auckland") under the `neighborhood`
 *  context entry (occasionally `locality`). We map that into
 *  `addressLine2` since our `addressSchema` doesn't have a dedicated
 *  suburb field — and "Suburb" is a more natural Line-2 use than the
 *  unit/apt number it traditionally holds. */
export function extractAddressParts(feature: MapboxFeature): AddressParts {
  const ctx = feature.context ?? [];
  const lookup = (prefix: string) =>
    ctx.find((c) => String(c.id).startsWith(prefix))?.text ?? '';
  const line1 = feature.address ? `${feature.address} ${feature.text}` : feature.text;
  // Prefer `neighborhood` (Mapbox's term for suburb in NZ); fall back to
  // `locality` for the rare cases the suburb is keyed under that prefix.
  const suburb = lookup('neighborhood.') || lookup('locality.');
  return {
    addressLine1: line1,
    addressLine2: suburb,
    city: lookup('place.'),
    stateProvince: lookup('region.'),
    postalCode: lookup('postcode.'),
    country: lookup('country.'),
  };
}

/** ISO 3166-1 alpha-2 country code(s) used to constrain geocoding to NZ.
 *  This is a fleet-maintenance app for an NZ operator — every relevant
 *  address (sites, assets, the map default) is in New Zealand, so we
 *  pin the country at the helper level rather than per call site. */
export const GEOCODE_COUNTRIES = 'nz';

/** Forward-geocode a free-text query. Caller decides whether to enable
 *  `autocomplete` (typeahead) or run a one-shot lookup. Results are
 *  always restricted to {@link GEOCODE_COUNTRIES}. */
export async function mapboxForwardGeocode(
  query: string,
  opts: { autocomplete?: boolean; limit?: number } = {},
): Promise<MapboxFeature[]> {
  if (!query.trim()) return [];
  // An empty token cannot produce a result, and sending it asks Mapbox to
  // reject us — say so once, here, instead of via a 401 the caller discards.
  if (!MAPBOX_TOKEN) {
    console.warn(
      '[mapbox] no access token — address lookup disabled. MAPBOX_TOKEN is baked '
      + 'into the bundle at BUILD time (webpack DefinePlugin), so it must be set in '
      + 'the build environment and the client REBUILT; setting it at runtime, or '
      + 'restarting without a rebuild, has no effect.',
    );
    return [];
  }
  const params = new URLSearchParams({
    access_token: MAPBOX_TOKEN,
    limit: String(opts.limit ?? 5),
    autocomplete: String(opts.autocomplete ?? false),
    country: GEOCODE_COUNTRIES,
  });
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?${params}`;
  const res = await fetch(url);
  if (!res.ok) {
    // Every failure used to return [] silently, which renders identically to
    // "no matches" — so a revoked token, a URL restriction and a stale cached
    // bundle all presented as "the Address field doesn't work". Naming the
    // status turns each into a one-look diagnosis:
    //
    //   401  Mapbox does not recognise the token — revoked, rotated, or never valid
    //   403  the token is real but not allowed for THIS request — a URL
    //        restriction on the Mapbox account (it matches on the Referer
    //        header, so a `no-referrer` policy fails from every origin), or a
    //        token without the Geocoding scope
    //   429  rate limited
    console.warn(`[mapbox] geocoding failed: HTTP ${res.status}`
      + `${res.status === 401 ? ' — token not recognised (revoked/rotated?)' : ''}`
      + `${res.status === 403 ? ' — token exists but is not permitted here (URL restriction or missing scope)' : ''}`
      + `${res.status === 429 ? ' — rate limited' : ''}`);
    return [];
  }
  const json = await res.json();
  return Array.isArray(json.features) ? json.features : [];
}
